import React, { Fragment, useEffect } from 'react'
import { Button, Input } from '@nextui-org/react'
import { inputs } from './constants'
import {
  handlePriceChange,
  handleKeyPress,
  priceFields,
  getAnnualTaxableIncome,
} from './controller'
import { formStyles, resumeStyles } from './styles'
import { Utils } from 'helpers/index'
import PropTypes from 'prop-types'
import Resume from './Resume'

// Valor UTA y MONTHLY_UTA siempre son fijos, se actualizan mensualmente.
const UTA = 15
const MONTHLY_UTA = 777516
const PRESUMED_BILLS_UTA = UTA * MONTHLY_UTA

const handleNumber = (value = '') => Number(value.toString().replaceAll('.', ''))

const Form = ({
  watch,
  register,
  setValue,
  errors,
  loading,
  handleSubmit,
  isDisabled,
  setResult,
}) => {
  const formattedWatch = () => {
    // para todas las propiedades de watch, aplicar handleNumber y devolver el nuevo objeto
    return Object.keys(watch()).reduce((acc, key) => {
      acc[key] = handleNumber(watch()[key])
      return acc
    }, {})
  }

  const { ticketFees, taxableIncomeRental, taxableIncomeInvestments, otherTaxableIncomes } =
    formattedWatch()

  // Gastos presuntos
  const presumedBills =
    ticketFees * 0.3 < PRESUMED_BILLS_UTA ? ticketFees * 0.3 : PRESUMED_BILLS_UTA
  // Renta imponible honorarios
  const taxableIncome = ticketFees - presumedBills
  // Renta imponible total
  const totalTaxableIncomes =
    taxableIncome + taxableIncomeRental + taxableIncomeInvestments + otherTaxableIncomes
  // Tabla de escala seleccionada
  const currentScale = getAnnualTaxableIncome(totalTaxableIncomes)
  // Cantidad a rebajar
  const amountToBeReduced = MONTHLY_UTA * currentScale?.amountToBeReduced
  // Impuesto a pagar
  const taxToPay = totalTaxableIncomes * currentScale?.factor - amountToBeReduced

  const resumes = {
    billsAndFees: [
      {
        text: 'Gastos presuntos',
        value: `$ ${Utils.formatRoundPrice(presumedBills)}`,
        classNames: resumeStyles.inputMutedError,
      },
      {
        text: 'Renta imponible honorarios',
        value: `$ ${Utils.formatRoundPrice(taxableIncome)}`,
        classNames: resumeStyles.inputMuted,
      },
    ],
    taxableIncomes: [
      {
        text: 'TOTAL RENTA IMPONIBLE',
        value: `$ ${Utils.formatRoundPrice(totalTaxableIncomes)}`,
        classNames: resumeStyles.contained,
      },
      {
        text: 'Tramo',
        value: currentScale?.section,
        classNames: resumeStyles.containedSmall,
      },
      {
        text: 'Factor',
        value: currentScale?.factor === 0 ? 'Exento' : `${currentScale?.factor}%`,
        classNames: { container: 'col-span-1 text-center font-bold', value: 'text-[#333333]' },
      },
      {
        text: 'Cantidad a rebajar',
        value: `$ ${Utils.formatRoundPrice(amountToBeReduced)}`,
        classNames: resumeStyles.inputMuted,
      },
    ],
  }

  useEffect(() => {
    setResult(Utils.formatRoundPrice(taxToPay))
  }, [taxToPay])

  return (
    <form className={formStyles.MAIN_CONTAINER}>
      {inputs.map((input, index) => {
        if (input.type === 'resume') {
          return <Resume key={index} items={resumes[input.resumeKey]} />
        }
        return (
          <Fragment key={index}>
            <p
              className={`${formStyles.LABEL} ${
                input.name === 'presumedBills' && 'text-[#EC0000]'
              }`}
            >
              {input.label}
            </p>
            <Input
              // label={input.label}
              type={input.type}
              name={input.name}
              className={input.className}
              color={errors[input.name] ? 'danger' : 'primary'}
              placeholder={input.placeholder}
              {...register(input.name)}
              errorMessage={errors[input.name]?.message}
              isRequired={input.isRequired}
              variant="bordered"
              aria-label="input"
              aria-labelledby="input"
              defaultValue={watch()[input.name]}
              onChange={(e) => handlePriceChange(input.name, e, setValue)}
              onKeyDown={(e) => handleKeyPress(input.name, e, setValue)}
              classNames={{ label: 'hidden', inputWrapper: 'rounded-3xl ' }}
              startContent={
                priceFields.includes(input.name) && (
                  <span className="text-sm text-[#8D8E92]">$</span>
                )
              }
            />
          </Fragment>
        )
      })}
      <div className="flex justify-center w-full col-span-2 mt-6">
        <Button
          type="submit"
          color="primary"
          size="lg"
          className="font-bold rounded-3xl"
          variant="shadow"
          isLoading={loading}
          isDisabled={isDisabled}
          onClick={() => handleSubmit()}
        >
          Ver resultados
        </Button>
      </div>
    </form>
  )
}

Form.propTypes = {
  watch: PropTypes.func.isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  setResult: PropTypes.func.isRequired,
}

export default Form
